/**
 * SmartFollowupCards — three-question suggestion bar that appears after a
 * Deep Explorer turn completes. Each card shows the question + a preview
 * of the pre-drafted mini-plan on hover; click sends the question (with
 * the mini-plan hint) as the next user turn.
 */
import { useState } from "react"

import { AnimatePresence, motion } from "motion/react"
import { useTranslation } from "react-i18next"

import type { FollowupSuggestion } from "./SmartFollowupCards-utils"

import { ArrowRight, Sparkles } from "@/components/icons"
import { cn } from "@/lib/utils"

interface SmartFollowupCardsProps {
  suggestions: FollowupSuggestion[]
  onSelect: (suggestion: FollowupSuggestion) => void
}

export function SmartFollowupCards({ suggestions, onSelect }: SmartFollowupCardsProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<number | null>(null)

  if (suggestions.length === 0) return null

  return (
    <section
      aria-label={t("deepExplorer.smart_followups.aria")}
      className="border-primary/30 bg-primary/5 mt-4 rounded-lg border border-dashed p-3"
    >
      <header className="mb-2 flex items-center gap-1.5">
        <Sparkles className="text-primary size-3.5" />
        <span className="text-primary text-[11px] font-semibold tracking-wide uppercase">
          {t("deepExplorer.smart_followups.title")}
        </span>
        <span className="text-muted-foreground text-[11px]">
          - {t("deepExplorer.smart_followups.subtitle")}
        </span>
      </header>
      <ul className="flex flex-col gap-2">
        {suggestions.slice(0, 3).map((suggestion, i) => {
          const isExpanded = expanded === i
          return (
            <li key={`${i}-${suggestion.question}`}>
              <button
                type="button"
                className={cn(
                  "group border-border bg-card w-full rounded-md border p-2.5 text-left text-sm transition-[border-color,box-shadow]",
                  "hover:border-primary/60 focus-visible:ring-ring hover:shadow-sm focus-visible:ring-2 focus-visible:outline-none",
                  isExpanded && "border-primary/60"
                )}
                onBlur={() => setExpanded(null)}
                onClick={() => onSelect(suggestion)}
                onFocus={() => setExpanded(i)}
                onMouseEnter={() => setExpanded(i)}
                onMouseLeave={() => setExpanded(null)}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-foreground flex-1 leading-snug">{suggestion.question}</span>
                  <ArrowRight
                    className={cn(
                      "text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform",
                      "group-hover:text-primary group-hover:translate-x-0.5"
                    )}
                  />
                </div>
                <AnimatePresence initial={false}>
                  {isExpanded && suggestion.miniPlan.length > 0 && (
                    <motion.div
                      key="mini-plan"
                      animate={{ opacity: 1, height: "auto" }}
                      className="overflow-hidden"
                      exit={{ opacity: 0, height: 0 }}
                      initial={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                      <div
                        className="text-muted-foreground border-border/60 mt-2 border-t pt-2 text-xs"
                        role="tooltip"
                      >
                        <p className="mb-1 text-[10px] font-semibold tracking-wide uppercase">
                          {t("deepExplorer.smart_followups.plan_title")}
                        </p>
                        <ol className="ml-4 list-decimal space-y-0.5">
                          {suggestion.miniPlan.map((step, j) => (
                            <li key={j} className="leading-snug">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
